import {
  ComponentProps,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DocumentTextIcon } from '@heroicons/react/24/outline';
import { JSONContent } from '@tiptap/core';
import { toast } from 'react-toastify';
import {
  CommunicationType,
  ICommunicationsResponseItem,
  IInboxConversationNote,
  IInboxConversationUser,
  InboxConversationChannel,
  InboxConversationStatus,
  UserTypes,
} from 'shared';

import { Badge } from '../../../common/Atoms/Badge';
import { Button } from '../../../common/Atoms/Button';
import { Select } from '../../../common/Atoms/Select';
import { Paragraph } from '../../../common/Atoms/Typography/Paragraph';
import { emailAddresses } from '../../../common/lib/emailAddresses';
import { useCurrentOrgUsers } from '../../hooks/useCurrentOrgUsers';
import { useInboundEmailAttachmentDownload } from '../../hooks/useInboundEmailAttachmentDownload';
import {
  useAddInboxConversationNoteMutation,
  useArchiveInboxConversationMutation,
  useAssignInboxConversationContactMutation,
  useAssignInboxConversationMutation,
  useGetInboxConversationQuery,
  useMarkInboxConversationReadMutation,
  useReopenInboxConversationMutation,
  useResolveInboxConversationMutation,
} from '../../services/api/inboxConversationApi/inboxConversation';
import { ContactAvatar } from '../Agents/Conversations/ContactAvatar';
import { findMentions } from '../Agents/Conversations/ConversationComposer';
import { formatLocalTimestamp } from '../Agents/Conversations/conversationHelpers';
import { ContactSearch, IPickedContact } from '../Common/ContactSearch';
import { EmailCommunication } from '../CRM/ContactPage/ContactTableSection/Communications/EmailCommunication';
import { InboundEmailCommunication } from '../CRM/ContactPage/ContactTableSection/Communications/InboundEmailCommunication';
import { SmsCommunication } from '../CRM/ContactPage/ContactTableSection/Communications/SmsCommunication';
import { EmailReplyComposer } from '../Molecules/EmailReplyComposer';
import { LoadingOverlay } from '../Molecules/LoadingOverlay';
import { SmsReplyComposer } from '../Molecules/SmsReplyComposer';
import { TextEditor } from '../Molecules/TextEditor/TextEditor';
import { WrappedLink } from '../WrappedLink';

type TextEditorValue = ComponentProps<typeof TextEditor>[`value`];

type TranscriptEntry =
  | { at: number; key: string; communication: ICommunicationsResponseItem }
  | { at: number; key: string; note: IInboxConversationNote };

const NOTE_MAX_LENGTH = 1500;
const UNASSIGNED = `unassigned`;

const STATUS_BADGES: Record<InboxConversationStatus, { label: string; color: `blue` | `green` | `gray` }> = {
  [InboxConversationStatus.OPEN]: { label: `Open`, color: `blue` },
  [InboxConversationStatus.RESOLVED]: { label: `Resolved`, color: `green` },
  [InboxConversationStatus.ARCHIVED]: { label: `Archived`, color: `gray` },
};

function userName(user: IInboxConversationUser | null) {
  if (!user) {
    return `Unknown user`;
  }

  return `${user.firstName || ``} ${user.lastName || ``}`.trim() || user.email;
}

function communicationTime(communication: ICommunicationsResponseItem) {
  const createdAt = communication.email?.createdAt || communication.inboundEmail?.createdAt || communication.sms?.createdAt;

  return createdAt ? new Date(createdAt).getTime() : 0;
}

function NoteComposer({ organisationId, conversationId }: { organisationId: string; conversationId: string }) {
  const currentOrgUsers = useCurrentOrgUsers(false, [UserTypes.Inherit, UserTypes.Invited]);
  const [addNote, { isLoading }] = useAddInboxConversationNoteMutation();
  const [note, setNote] = useState<{ content: string; json: JSONContent | null }>({ content: ``, json: null });
  const tooLong = note.content.length > NOTE_MAX_LENGTH;

  const onSubmit = async() => {
    try {
      await addNote({
        organisationId,
        conversationId,
        content: note.content.trim(),
        mentionedUsers: findMentions(note.json, currentOrgUsers).map(mention => mention.value),
      }).unwrap();

      setNote({ content: ``, json: null });
    }
    catch {
      toast.error(`Could not add internal note.`);
    }
  };

  return (
    <div className={ `rounded-lg border border-amber-200 bg-amber-50/60` }>
      <TextEditor
        mode={ `note` }
        value={ (note.json || ``) as TextEditorValue }
        onChange={ ({ text, json }) => setNote({ content: text, json }) }
        mentionValues={ currentOrgUsers.map(user => user.label) }
        placeholder={ `Write an internal note (not sent to the customer)... use @ to mention someone` }
        editorClassname={ `min-h-[64px] border-transparent bg-transparent px-3 py-2 text-sm hover:border-transparent focus:border-transparent` }
      />
      {
        tooLong &&
        <Paragraph
          variant={ `error` }
          size={ `xs` }
          className={ `px-3` }
        >
          { `Note must be less than ${NOTE_MAX_LENGTH} characters` }
        </Paragraph>
      }
      <div className={ `flex justify-end px-2 pb-2 pt-1` }>
        <Button
          size={ `sm` }
          disabled={ isLoading || tooLong || !note.content.trim() }
          onClick={ () => void onSubmit() }
        >
          { isLoading ? `Adding note...` : `Add internal note` }
        </Button>
      </div>
    </div>
  );
}

export function ConversationDetail({ organisationId, conversationId }: { organisationId: string; conversationId: string }) {
  const args = { organisationId, conversationId };
  // currentData, not data: while the next conversation loads, data still holds the previous one
  const { currentData: data, error } = useGetInboxConversationQuery(args, {
    pollingInterval: 15000,
    skipPollingIfUnfocused: true,
  });
  const assignableUsers = useCurrentOrgUsers(false, [UserTypes.Inherit, UserTypes.Invited]);
  const { downloadAttachment, downloading } = useInboundEmailAttachmentDownload();
  const [markRead] = useMarkInboxConversationReadMutation();
  const [resolve, { isLoading: isResolving }] = useResolveInboxConversationMutation();
  const [archive, { isLoading: isArchiving }] = useArchiveInboxConversationMutation();
  const [reopen, { isLoading: isReopening }] = useReopenInboxConversationMutation();
  const [assign, { isLoading: isAssigning }] = useAssignInboxConversationMutation();
  const [assignContact, { isLoading: isAssigningContact }] = useAssignInboxConversationContactMutation();
  const [pickedContact, setPickedContact] = useState<IPickedContact>();
  const [composer, setComposer] = useState<`reply` | `note`>(`reply`);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const conversation = data?.conversation;
  const unread = conversation?.unread;

  // Opening a conversation reads it, and so does a message arriving while it is open
  useEffect(() => {
    if (unread) {
      void markRead({ organisationId, conversationId });
    }
  }, [unread, organisationId, conversationId, markRead]);

  const transcript = useMemo(() => {
    const entries: TranscriptEntry[] = [
      ...(data?.communications || []).map((communication, index) => ({
        at: communicationTime(communication),
        key: `communication-${index}`,
        communication,
      })),
      ...(data?.notes || []).map(note => ({
        at: new Date(note.createdAt).getTime(),
        key: `note-${note.id}`,
        note,
      })),
    ];

    // Stable, so messages sharing a timestamp keep the order the API gave them
    return entries.sort((a, b) => a.at - b.at);
  }, [data?.communications, data?.notes]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: `end` });
  }, [conversationId, transcript.length]);

  if (error) {
    return (
      <Paragraph variant={ `error` }>
        { `Couldn't load this conversation.` }
      </Paragraph>
    );
  }

  if (!conversation) {
    return (
      <LoadingOverlay
        loading
        className={ `min-h-[200px]` }
      >
        <div />
      </LoadingOverlay>
    );
  }

  const isOpen = conversation.status === InboxConversationStatus.OPEN;
  const statusBadge = STATUS_BADGES[conversation.status];
  const latestInboundEmail = [...(data?.communications || [])].reverse().find(item => item.inboundEmail)?.inboundEmail;
  const latestSms = [...(data?.communications || [])].reverse().find(item => item.sms)?.sms;
  const smsNumber = latestSms?.direction === `inbound` ? latestSms.from : latestSms?.to;
  const replySubject = /^re:/i.test(latestInboundEmail?.subject || ``) ?
    latestInboundEmail?.subject :
    `Re: ${latestInboundEmail?.subject || conversation.title || ``}`.trim();

  const onAssignContact = async() => {
    if (!pickedContact) {
      return;
    }

    try {
      await assignContact({ ...args, contactId: pickedContact.id }).unwrap();
      setPickedContact(undefined);
    }
    catch {
      toast.error(`Could not assign the contact.`);
    }
  };

  return (
    <div className={ `space-y-4` }>
      <div className={ `rounded-xl border border-gray-200 bg-linear-to-r from-white to-gray-50/70 p-4 shadow-xs` }>
        <div className={ `flex items-start justify-between gap-3` }>
          <div className={ `flex min-w-0 items-start gap-3` }>
            <ContactAvatar name={ conversation.contactName || conversation.sender } />
            <div className={ `min-w-0` }>
              <div className={ `flex flex-wrap items-center gap-2` }>
                <Paragraph variant={ `link` }>
                  {
                    conversation.contactId ?
                      <WrappedLink
                        to={ `/contacts/${conversation.contactId}` }
                        className={ `hover:underline` }
                      >
                        { conversation.contactName || `Unknown contact` }
                      </WrappedLink> :
                      (conversation.sender || `Unknown sender`)
                  }
                </Paragraph>
                {
                  !conversation.contactId &&
                  <Badge color={ `orange` }>
                    { `No contact matched` }
                  </Badge>
                }
                {
                  conversation.invoiceId &&
                  <WrappedLink
                    to={ `/invoices/${conversation.invoiceId}` }
                    className={ `rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs text-gray-500 transition hover:border-gray-300 hover:bg-gray-100 hover:text-gray-700` }
                  >
                    { `View invoice` }
                  </WrappedLink>
                }
                <Badge color={ statusBadge.color }>
                  { statusBadge.label }
                </Badge>
              </div>
              <Paragraph
                size={ `xs` }
                variant={ `secondary` }
                className={ `mt-1 truncate` }
              >
                { conversation.title || (conversation.channel === InboxConversationChannel.SMS ? `SMS` : `No subject`) }
              </Paragraph>
            </div>
          </div>
          <div className={ `flex shrink-0 items-center gap-2` }>
            {
              isOpen ?
                <Button
                  size={ `sm` }
                  disabled={ isResolving }
                  onClick={ () => void resolve(args) }
                >
                  { `Resolve` }
                </Button> :
                <Button
                  size={ `sm` }
                  color={ `gray` }
                  disabled={ isReopening }
                  onClick={ () => void reopen(args) }
                >
                  { `Reopen` }
                </Button>
            }
            {
              conversation.status !== InboxConversationStatus.ARCHIVED &&
              <Button
                size={ `sm` }
                color={ `gray` }
                disabled={ isArchiving }
                onClick={ () => void archive(args) }
              >
                { `Archive` }
              </Button>
            }
          </div>
        </div>
        <div className={ `mt-3 flex items-center gap-2 text-xs text-gray-500` }>
          { `Assigned to` }
          <Select
            options={ [
              { value: UNASSIGNED, label: `Unassigned` },
              // Someone since removed from the organisation can still be the assignee
              ...(conversation.assignee && !assignableUsers.some(user => user.value === conversation.assignee?.id) ?
                [{ value: conversation.assignee.id, label: userName(conversation.assignee) }] :
                []),
              ...assignableUsers,
            ] }
            selected={ conversation.assignee?.id || UNASSIGNED }
            disabled={ isAssigning }
            onChange={ ({ value }) => void assign({ ...args, userId: value === UNASSIGNED ? null : String(value) }) }
            size={ `sm` }
            className={ `w-64 min-w-0` }
          />
        </div>
      </div>

      <div className={ `rounded-lg border border-gray-200 bg-white p-4 shadow-xs` }>
        {
          !transcript.length &&
          <Paragraph
            size={ `xs` }
            variant={ `secondary` }
          >
            { `Nothing has been said yet.` }
          </Paragraph>
        }
        <div className={ `space-y-6` }>
          {
            transcript.map(entry => {
              if (`note` in entry) {
                return (
                  <div
                    key={ entry.key }
                    className={ `rounded-lg border border-amber-200 bg-amber-50/50 p-3` }
                  >
                    <div className={ `flex items-center justify-between gap-2` }>
                      <div className={ `flex items-center gap-2` }>
                        <DocumentTextIcon className={ `h-4 w-4 text-amber-700` } />
                        <Paragraph
                          size={ `xs` }
                          className={ `font-medium text-amber-900` }
                        >
                          { `Internal note` }
                        </Paragraph>
                        <Paragraph
                          size={ `xs` }
                          variant={ `secondary` }
                        >
                          { userName(entry.note.author) }
                        </Paragraph>
                      </div>
                      <span className={ `text-xs text-amber-700/80` }>
                        { formatLocalTimestamp(String(entry.note.createdAt)) }
                      </span>
                    </div>
                    <Paragraph
                      size={ `sm` }
                      className={ `mt-2 whitespace-pre-wrap text-amber-950` }
                    >
                      { entry.note.content }
                    </Paragraph>
                    {
                      entry.note.mentions.length > 0 &&
                      <Paragraph
                        size={ `xs` }
                        className={ `mt-2 text-amber-800` }
                      >
                        { `Mentioned: ${entry.note.mentions.map(userName).join(`, `)}` }
                      </Paragraph>
                    }
                  </div>
                );
              }

              const { email, inboundEmail, sms } = entry.communication;

              if (entry.communication.type === CommunicationType.EMAIL && email) {
                return (
                  <EmailCommunication
                    key={ entry.key }
                    email={ email }
                  />
                );
              }

              if (entry.communication.type === CommunicationType.INBOUND_EMAIL && inboundEmail) {
                return (
                  <InboundEmailCommunication
                    key={ entry.key }
                    inboundEmail={ inboundEmail }
                    showFullEmail
                    onDownloadAttachment={ (index, name) => void downloadAttachment(inboundEmail.id, index, name) }
                    downloadingAttachmentIndex={ downloading?.inboundEmailId === inboundEmail.id ? downloading.index : null }
                  />
                );
              }

              if (entry.communication.type === CommunicationType.SMS && sms) {
                return (
                  <SmsCommunication
                    key={ entry.key }
                    sms={ sms }
                  />
                );
              }

              return null;
            })
          }
        </div>

        <div className={ `mt-6 flex gap-1 border-t border-gray-200 pt-3` }>
          {
            ([[`reply`, conversation.channel === InboxConversationChannel.SMS ? `Reply by SMS` : `Reply by email`], [`note`, `Internal note`]] as const).map(([value, label]) => (
              <button
                key={ value }
                type={ `button` }
                aria-pressed={ composer === value }
                className={ `cursor-pointer rounded-md px-2.5 py-1 text-sm font-semibold transition-colors ${composer === value ? `bg-gray-100 text-gray-900` : `text-gray-500 hover:bg-gray-50`}` }
                onClick={ () => setComposer(value) }
              >
                { label }
              </button>
            ))
          }
        </div>

        <div className={ `mt-2` }>
          {
            composer === `note` &&
            <NoteComposer
              organisationId={ organisationId }
              conversationId={ conversationId }
            />
          }
          {
            // A reply is sent as the contact's on demand email or SMS, so it needs a contact first
            composer === `reply` && !conversation.contactId &&
            <div className={ `relative space-y-2 rounded-lg border border-orange-200 bg-orange-50/40 p-3` }>
              <Paragraph size={ `sm` }>
                { `This email could not be matched to a contact. Assign one to reply.` }
              </Paragraph>
              <ContactSearch
                label={ `Contact` }
                selected={ pickedContact }
                onSelect={ setPickedContact }
                compact
              />
              <Button
                size={ `sm` }
                disabled={ !pickedContact || isAssigningContact }
                onClick={ () => void onAssignContact() }
              >
                { isAssigningContact ? `Assigning...` : `Assign contact` }
              </Button>
            </div>
          }
          {
            composer === `reply` && !!conversation.contactId && conversation.channel === InboxConversationChannel.EMAIL &&
            <EmailReplyComposer
              organisationId={ organisationId }
              contactId={ conversation.contactId }
              defaultRecipients={ [...new Set([
                ...emailAddresses(latestInboundEmail?.forwardedFrom || latestInboundEmail?.from),
                ...emailAddresses(latestInboundEmail?.cc),
              ])] }
              defaultSubject={ replySubject }
              replyToInboundEmailId={ latestInboundEmail?.id }
              titleLabel={ `Reply to this email` }
              bodyPlaceholder={ `Type your reply...` }
              showSubjectInput={ false }
              containerClassName={ `` }
            />
          }
          {
            composer === `reply` && !!conversation.contactId && conversation.channel === InboxConversationChannel.SMS &&
            <SmsReplyComposer
              organisationId={ organisationId }
              contactId={ conversation.contactId }
              destinationNumber={ smsNumber }
              hasSmsDestination={ !!smsNumber }
              sendSmsDisabledReason={ smsNumber ? `` : `No phone number available to send SMS to.` }
            />
          }
        </div>
        <div ref={ bottomRef } />
      </div>
    </div>
  );
}
